import { Button, TruncateText, ToggleSwitch, Modal, ModalHeader, ModalBody, ModalFooter, RenderModalProps, useModal } from '@panwds/react-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from "../../components";
import { Table } from '../../components/PANWDSElements';
import { usePermissions, useTranslate } from '../../customHooks';
import { CircleCheckIcon, ExclamationTriangleIcon } from '@panwds/icons';
import UserManagementSheet from './UserManagementSheet';
import { ColumnOptions, Data, RowInstance } from '@panwds/react-table';
import { SelectFilter } from '@panwds/react-filters';
import { useCreateUserMutation, useGetUsersQuery, useUpdateUserMutation, useDeleteUsersMutation } from '../../redux/services/user-roles-service';
import { useGetTenantSettingsQuery, useUpdateTenantSettingsMutation } from '../../redux/services/settings-service';
import { get } from "lodash";
import { ApiSaasRolePermission, UserDetails } from "../../interfaces/interfaces";
import { getUserName } from "../../utils/utils";

const handleRolesValueFormat = (userData: ApiSaasRolePermission[]) => {
  return userData.map((permissionPolicy: ApiSaasRolePermission) => {
    if (permissionPolicy.AccountId) {
      return `${permissionPolicy.AccountId}/${permissionPolicy.Policy}`
    } else {
      return `${permissionPolicy.Policy}`
    }
  });
}

const handleRolesInputFormatter = (value: any) => {
  const values = value.split('/');
  let o: any = {};
  if (values.length > 1) {
    o.AccountId = values[0];
    o.Policy = values[1];
  } else {
    o.Policy = values[0];
  }
  return o;
}

interface CrossDomainAccessUpdateProps {
  update: () => Promise<void>;
}

const CrossDomainAccessConfirmationDialogModal = ({
  closeModal,
  isOpen,
  update,
}: RenderModalProps<CrossDomainAccessUpdateProps>) => {
  const translate = useTranslate();
  return <Modal
    dataMetrics="cloudngfw-cross-domain-access-confirm-modal"
    onClose={closeModal}
    isOpen={isOpen}
    size='md'
  >
    <ModalHeader title={translate('generic.notification')} />
    <ModalBody>
      <div>
        {translate('resources.tokens.crossDomainAccess.confirmEnableTitle')}
      </div>
      <div style={{ marginTop: '16px' }}>
        {translate('resources.tokens.crossDomainAccess.confirmEnableDescription')}
      </div>
    </ModalBody>
    <ModalFooter>
      <Button onClick={closeModal} appearance="secondary">
        {translate('generic.cancel')}
      </Button>
      <Button onClick={update} appearance="primary">
        {translate('generic.confirm')}
      </Button>
    </ModalFooter>
  </Modal>;
};

const CrossDomainAccessDisableErrorModal = ({
  closeModal,
  isOpen,
}: RenderModalProps<{}>) => {
  const translate = useTranslate();
  return <Modal
    dataMetrics="cloudngfw-cross-domain-access-error-modal"
    onClose={closeModal}
    isOpen={isOpen}
    size='md'
  >
    <ModalHeader
      icon={<ExclamationTriangleIcon size="md" className="tw-text-red-500 dark:tw-text-dark-bg-red" />}
      title={translate('resources.tokens.crossDomainAccess.errorDisableTitle')}
    />
    <ModalBody>
      <div>
        {translate('resources.tokens.crossDomainAccess.errorDisableMessage')}
      </div>
      <div style={{ marginTop: '16px' }}>
        {translate('resources.tokens.crossDomainAccess.errorDisableDescription')}
      </div>
    </ModalBody>
    <ModalFooter>
      <Button onClick={closeModal} appearance="primary">
        {translate('generic.close')}
      </Button>
    </ModalFooter>
  </Modal>;
};

interface InviteExternalErrorProps {
  domain: string;
  onEnableClick: () => void;
}

const CrossDomainAccessInviteErrorModal = ({
  closeModal,
  isOpen,
  domain,
  onEnableClick,
}: RenderModalProps<InviteExternalErrorProps>) => {
  const translate = useTranslate();

  const handleEnableClick = () => {
    closeModal();
    onEnableClick();
  };

  return <Modal
    dataMetrics="cloudngfw-cross-domain-invite-error-modal"
    onClose={closeModal}
    isOpen={isOpen}
    size='md'
  >
    <ModalHeader
      icon={<ExclamationTriangleIcon size="md" className="tw-text-red-500 dark:tw-text-dark-bg-red" />}
      title={translate('resources.tokens.crossDomainAccess.errorInviteTitle')}
    />
    <ModalBody>
      <div>
        {translate('resources.tokens.crossDomainAccess.errorInviteMessage', { domain })}
      </div>
      <div style={{ marginTop: '16px' }}>
        {translate('resources.tokens.crossDomainAccess.errorInviteDescription')}{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleEnableClick();
          }}
          style={{ color: '#0066cc', textDecoration: 'underline', cursor: 'pointer' }}
        >
          {translate('resources.tokens.crossDomainAccess.errorInviteEnableLink')}
        </a>.
      </div>
    </ModalBody>
    <ModalFooter>
      <Button onClick={closeModal} appearance="primary">
        {translate('generic.close')}
      </Button>
    </ModalFooter>
  </Modal>;
};

type PanelType = "" | "inviteUser" | "updateUser";

const UsersListV2 = () => {

  const translate = useTranslate();
  const [sheet, setSheet] = useState<PanelType>("");
  const { permissions } = usePermissions();
  const [currentUser, setCurrentuser] = useState<UserDetails | undefined>(undefined);
  const [createUser, { isLoading: creatingUser }] = useCreateUserMutation();
  const [updateUser, { isLoading: updatingUser }] = useUpdateUserMutation();
  const [deleteUsersApi, { isLoading: deletingUsers }] = useDeleteUsersMutation();

  const {
    data: { CrossEnterpriseDomainAccess, CanEnableCrossEnterpriseDomainAccess } = {},
    error: tenantSettingsError, isLoading: tenantSettingsLoading
  } = useGetTenantSettingsQuery({}, {});
  const [updateTenantSettings] = useUpdateTenantSettingsMutation();

  const {
    openModal: crossDomainAccessOpenModal, closeModal: crossDomainAccessCloseModal
  } = useModal(CrossDomainAccessConfirmationDialogModal);

  const {
    openModal: crossDomainAccessErrorOpenModal, closeModal: crossDomainAccessErrorCloseModal
  } = useModal(CrossDomainAccessDisableErrorModal);

  const {
    openModal: inviteExternalErrorOpenModal, closeModal: inviteExternalErrorCloseModal
  } = useModal(CrossDomainAccessInviteErrorModal);

  const { data: usersInfo, error, isLoading: usersListLoading, isFetching: usersListFetching } = useGetUsersQuery({ MaxResults: 25, Describe: true }, {
    // Keep the query alive to receive updates
    skip: !permissions?.ListUsers,
    refetchOnMountOrArgChange: false,
    refetchOnReconnect: false,
  });

  if (error) {
    toast.error(error?.error, { toastId: "list-users" });
  }

  if (tenantSettingsError) {
    toast.error(tenantSettingsError?.error, { toastId: "tenant-settings-describe" });
  }

  useEffect(() => {
    if (permissions && !permissions?.ListUsers) {
      toast.warning(translate("permissions.cantView"), { toastId: "list-users" });
    }
  }, [permissions]);

  const updateCrossEnterpriseDomainAccess = useCallback(
    async () => {
      const isEnabling = !CrossEnterpriseDomainAccess;
      try {
        await updateTenantSettings({ CrossEnterpriseDomainAccess: isEnabling }).unwrap();
        crossDomainAccessCloseModal();

        // Show success toast
        if (isEnabling) {
          toast.success(translate('resources.tokens.crossDomainAccess.successEnabled'), {
            toastId: "cross-domain-access-enabled"
          });
        } else {
          toast.success(translate('resources.tokens.crossDomainAccess.successDisabled'), {
            toastId: "cross-domain-access-disabled"
          });
        }
      }
      catch (error: any) {
        crossDomainAccessCloseModal();

        // If disabling failed due to external users, show error modal
        // Otherwise show generic error toast
        if (!isEnabling && (error?.error?.toLowerCase().includes("external") || error?.error?.toLowerCase().includes("domain"))) {
          crossDomainAccessErrorOpenModal({});
        } else {
          toast.error(`${error?.code}: ${error?.error}`, { toastId: "cross-domain-access-update" });
        }
      }
    }, [CrossEnterpriseDomainAccess, updateTenantSettings, crossDomainAccessCloseModal, crossDomainAccessErrorOpenModal, translate]
  );

  const filterConfig = [
    {
      Component: SelectFilter,
      context: {
        enableMultiSelect: true,
        items: [{
          value: 'TenantAdmin',
          children: 'Tenant Admin',
        }, {
          value: 'TenantReader',
          children: 'Tenant Reader',
        }, {
          value: 'GlobalFirewallAdmin',
          children: 'Global Firewall Admin',
        }, {
          value: 'LocalFirewallAdmin',
          children: 'Local Firewall Admin',
        }, {
          value: 'LocalRuleStackAdmin',
          children: 'Local Rulestack Admin',
        }, {
          value: 'InventoryAdmin',
          children: 'Inventory Admin',
        }],
      },
      label: translate('resources.firewallsV2.userRoles'),
      name: 'Permissions',
      static: true,
      useAndLogic: true,
      comparator: (
        item: string,
        itemVal: string[],
        row: Record<string, unknown>
      ) => {
        const rowValue = get(row, item);

        if (!Array.isArray(rowValue) || !Array.isArray(itemVal)) {
          return false;
        }

          return itemVal.every(keyword => rowValue.some(item => item.Policy === keyword));
      },
    },
  ];

  const columns = useMemo<ColumnOptions<any>[]>(
    () => [
      {
        accessor: 'UserName',
        Header: translate(`resources.users.fields.UserName`),
        minWidth: 250,
        Cell: ({ row }: { row: RowInstance<object> }) => {
          return (
            !permissions?.UpdateUser || row?.original?.UserStatus && (row?.original?.UserStatus.toLowerCase() === "provisioned" || row?.original?.UserStatus.toLowerCase() === "staged") ?
              <TruncateText>{row?.original?.UserName}</TruncateText>
              :
              <TruncateText
                addClassName="nameStyleCursor"
                onClick={(evt) => updateUserRecord(row, evt)}
              >
                {row?.original?.UserName}
              </TruncateText>
          )
        }
      },
      {
        accessor: 'Name',
        Header: translate(`resources.users.fields.Name`),
        minWidth: 200,
        Cell: ({ row }: { row: RowInstance<object> }) => {
          {
            return <TruncateText>
              {row?.original?.FirstName} {row?.original?.LastName}
            </TruncateText>
          }
        }
      },
      {
        accessor: 'UserStatus',
        Header: translate(`resources.users.fields.UserStatus`),
        Cell: ({ row }: { row: RowInstance<object> }) => {
          {
            let userPending = row?.original?.UserStatus && (row?.original?.UserStatus.toLowerCase() === "provisioned" ||
              row?.original?.UserStatus.toLowerCase() === "staged" ||
              row?.original?.UserStatus.toLowerCase() === "deprovisioned");
            let userActive = row?.original?.UserStatus && row?.original?.UserStatus.toLowerCase() === "active"
            return (
              <TruncateText>
                {(userActive) ?
                  <> <CircleCheckIcon
                    className="acceptedColor"
                    data-metrics="cloudngfw-users-list-CircleCheck-img"
                    size="sm"
                  /> {row?.original?.UserStatus}</> :
                  (userPending) ?
                    <><ExclamationTriangleIcon size="xs" className="pendingColor" /> {translate('resources.users.fields.StatusPending')}</>
                    : <>{row?.original?.UserStatus}</>
                }
              </TruncateText>
            )
          }
        }
      },
      {
        accessor: 'Roles',
        Header: translate(`resources.users.fields.Roles`),
        Cell: ({ row }: { row: RowInstance<object> }) => {
          let roles = row?.original?.Permissions?.map((item: any) => (item?.AccountId) ? `${(item?.AccountId)}/${item?.Policy}` : item?.Policy)
          let roleValues = (roles) ? roles.join(", ") : "";
          return <TruncateText>{roleValues}</TruncateText>
        }
      },
    ],
    []
  );

  const deleteUsers = useCallback(async (selectedUsers: UserDetails[]) => {
    const currentUser = getUserName();

    //Check if current user is trying to delete themselves
    if (selectedUsers.some(user => user.UserName === currentUser)) {
      toast.info(translate(`resources.users.fields.SelfDelete`));
      return;
    }

    const deletePromises = selectedUsers.map(async (userData) => {
      try {
        await deleteUsersApi({ UserName: userData.UserName }).unwrap();
        return { success: true, user: userData };
      } catch (error: any) {
        return {
          success: false, user: userData, error: error?.error
        };
      }
    });

    try {
      // Wait for all delete operations to complete
      const results = await Promise.allSettled(deletePromises);

      const successfulDeletes: UserDetails[] = [];
      const failedDeletes: { user: UserDetails; error: string }[] = [];

      // Process results
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { success, user, error } = result.value;
          if (success) {
            successfulDeletes.push(user);
          } else {
            failedDeletes.push({ user, error });
          }
        }
      });

      // Show success toast if any deletions succeeded
      if (successfulDeletes.length > 0) {
        toast.success(
          `${successfulDeletes.length} user(s) deleted successfully.`,
          { toastId: "users-delete-success" }
        );
      }

      // Show error toast with failed usernames
      if (failedDeletes.length > 0) {
        const failedUsernames = failedDeletes.map(item => item.user.UserName).join(', ');
        const errorMessage = `Failed to delete users: ${failedUsernames}`;

        toast.error(errorMessage, {
          toastId: "users-delete-error",
        });

      }

    } catch (error) {
    }
  }, [deleteUsersApi]);

  const batchActions = useCallback((selectedRows: Data[]) => {
    return {
      leftControl: CanEnableCrossEnterpriseDomainAccess && CrossEnterpriseDomainAccess !== undefined ? (
        <ToggleSwitch
          checked={CrossEnterpriseDomainAccess ?? false}
          onClick={() => {
            if (!CrossEnterpriseDomainAccess) {
              // Enabling: show risk acknowledgement modal first
              crossDomainAccessOpenModal({ update: updateCrossEnterpriseDomainAccess });
            } else {
              // Disabling: call API directly (backend enforces external user guard)
              updateCrossEnterpriseDomainAccess();
            }
          }}
          disabled={!(permissions?.UpdateCrossEnterpriseDomainAccess) || tenantSettingsLoading}
          dataMetrics={"cloudngfw-users-cross-domain-access"}
          labelPlacement="right"
          label={translate(`resources.tokens.crossDomainAccess.${CrossEnterpriseDomainAccess ? 'enabled' : 'disabled'}`)}
        />
      ) : null,
      rightControl: (permissions?.CreateUser &&
        <>
          <Button
            dataTestId="invite-user"
            onClick={() => setSheet("inviteUser")}
            appearance="primary"
            dataMetrics="cloudngfw-users-invite-btn"
          >
            {translate(`resources.users.fields.InviteUser`)}
          </Button>
          <Button
            dataTestId="delete-user"
            onClick={() => deleteUsers(selectedRows)}
            appearance="primary-destructive"
            dataMetrics="cloudngfw-users-delete-btn"
            disabled={usersListFetching || !selectedRows.length}
          >
            {translate(`generic.delete`)}
          </Button>
        </>
      )
    };
  }, [permissions?.CreateUser, permissions?.UpdateCrossEnterpriseDomainAccess, translate, deleteUsers, usersListFetching, CrossEnterpriseDomainAccess, CanEnableCrossEnterpriseDomainAccess, tenantSettingsLoading, crossDomainAccessOpenModal, updateCrossEnterpriseDomainAccess]);

  const onInviteUserSubmit = useCallback(
    async (data) => {
      try {
        data.Permissions = data.Permissions.map((item: string) => handleRolesInputFormatter(item));
        const result = await createUser(data).unwrap();
        if (result) {
          setSheet("");
        }
      }
      catch (error: any) {
        // Check if error is about unauthorized domain
        if (error?.error?.toLowerCase().includes("domain_restriction_enabled") ||
            (error?.error?.toLowerCase().includes("domain") && error?.error?.toLowerCase().includes("not authorized"))) {
          // Extract domain from email in data.UserName
          const email = data.UserName || "";
          const domain = email.split('@')[1] || "unknown";

          inviteExternalErrorOpenModal({
            domain,
            onEnableClick: () => {
              // Show the enable confirmation modal
              crossDomainAccessOpenModal({ update: updateCrossEnterpriseDomainAccess });
            }
          });
        } else {
          toast.error(error?.error, { toastId: "users-create" });
        }
      }
    }, [createUser, inviteExternalErrorOpenModal, crossDomainAccessOpenModal, updateCrossEnterpriseDomainAccess]
  );

  const onUpdateUserSubmit = useCallback(
    async (data) => {
      try {
        data.Permissions = data.Permissions.map((item: string) => handleRolesInputFormatter(item));
        const result = await updateUser(data).unwrap();
        if (result) {
          setSheet("");
        }
      }
      catch (error: any) {
        toast.error(error?.error, { toastId: "users-create" });
      }
    }, []
  );

  const updateUserRecord = useCallback((rowProps, event) => {
    setCurrentuser({ ...rowProps.original, Permissions: handleRolesValueFormat(rowProps.original.Permissions) });
    setSheet('updateUser')
  }, []);

  return (
    <>
      <div className="tw-overflow-table-container tw-flex tw-flex-col">
        <Table
          tableComponentData={usersInfo?.UserDetails || []}
          tableComponentColumns={columns}
          tableComponentEmptyStateProps={{
            heading: translate(`resources.users.fields.EmptyTitle`),
            description: translate(`resources.users.fields.EmptySubtitle`)
          }}
          tableComponentFilterBar={{
            hideAddFilter: true,
            filterConfig: filterConfig,
          }}
          tableBatchAction={batchActions}
          tableIsLoading={usersListLoading}
          tableisBackgroundFetching={usersInfo?.NextToken || usersListFetching || deletingUsers}
          tableTitle={translate(`resources.users.name`)}
          tableSubtitle={translate(`resources.users.fields.ListSubtitle`)}
          tableOffsetHeight={150}
          tableEnableRowSelect
          tableReadOnly={deletingUsers}
        />
        {sheet === 'inviteUser' && <UserManagementSheet
          close={() => setSheet("")}
          onSubmit={onInviteUserSubmit}
          userData={{}}
          loading={creatingUser}
        />}
        {sheet === 'updateUser' && <UserManagementSheet
          close={() => setSheet("")}
          onSubmit={onUpdateUserSubmit}
          userData={currentUser || {}}
          loading={updatingUser}
        />}
      </div>
    </>
  );

}

export default withRouter(UsersListV2);
